import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { getBlockLibraryCliApiBaseUrl } from '@modules/firebase-core/services/BlockLibraryMutationApi';
import type { CliStrategyRestoreData } from '@modules/shared-types/src/strategyVersionContent';

export async function restoreStrategyViaApi(projectId: string, strategyId: string, fromVersionId?: string): Promise<CliStrategyRestoreData> {
  const user = CoreFirebaseService.getInstance().auth.currentUser;
  if (!user) throw new Error('Sign in before restoring a strategy.');
  const base = getBlockLibraryCliApiBaseUrl().replace(/\/$/, '');
  const response = await fetch(`${base}/api/cli/v1/capabilities`);
  const capabilities = await response.json();
  if (!response.ok || capabilities?.success === false || capabilities?.data?.features?.transactionalStrategyRestore !== true) {
    throw new Error('The server does not support protected strategy restoration yet. Update the server before retrying.');
  }
  const token = await user.getIdToken();
  const send = async (dryRun: boolean, expectedRevision?: string): Promise<CliStrategyRestoreData> => {
    const result = await fetch(`${base}/api/cli/v1/projects/${encodeURIComponent(projectId)}/strategies/${encodeURIComponent(strategyId)}/restore`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ fromVersionId, dryRun, expectedRevision }),
    });
    const payload = await result.json();
    if (!result.ok || payload?.success === false) throw new Error(payload?.error?.message ?? payload?.detail ?? 'Strategy restore failed.');
    const data = payload?.data;
    if (data?.projectId !== projectId || data?.strategyId !== strategyId || data?.fromVersionId !== (fromVersionId ?? null)
      || data?.dryRun !== dryRun || data?.status !== 'draft' || typeof data?.restored !== 'boolean'
      || typeof data?.currentVersion !== 'string' || data.currentVersion.includes('/')
      || !/^[a-f0-9]{64}$/.test(data?.revision ?? '') || !/^[a-f0-9]{64}$/.test(data?.contentHash ?? '')
      || !Number.isSafeInteger(data?.blockCount) || data.blockCount < 0
      || !Number.isSafeInteger(data?.screenCount) || data.screenCount < 0) {
      throw new Error(`Strategy ${strategyId} returned an invalid restore receipt. Refresh before retrying.`);
    }
    return data;
  };
  const preview = await send(true);
  const applied = await send(false, preview.revision);
  if (['revision', 'contentHash', 'currentVersion', 'blockCount', 'screenCount', 'restored'].some(key =>
    preview[key as keyof CliStrategyRestoreData] !== applied[key as keyof CliStrategyRestoreData])) {
    throw new Error(`Strategy ${strategyId} restore differed from its preview. Refresh before retrying.`);
  }
  return applied;
}
